import type { ChangeEvent } from 'react';
import { useRef, useState } from 'react';

import { DataTestIds } from '@repo/frontend-shared';

import { filterOptions, sortOptions } from '../../../utils/sorting-and-filtering';
import { SearchBarDropdown } from './SearchBarDropdown/SearchBarDropdown';
import { SearchIcon } from './SearchIcon';

import './SearchBar.scss';

interface SearchBarProps<T> {
  options?: T[];
  getOptionLabel?: (option: T) => string;
  getOptionKey?: (option: T) => string | number;
  onChange?: (value: T) => void;
  onSearchChange?: (search: string) => void;
  inputPlaceholder?: string;
  showDropdown?: boolean;
  ariaLabel?: string;
  dataTestId?: string;
}

const EMPTY_SEARCH_OPTIONS: never[] = [];
const EMPTY_OPTION_LABEL = () => '';

export function SearchBar<T>({
  options = EMPTY_SEARCH_OPTIONS,
  getOptionLabel = EMPTY_OPTION_LABEL,
  getOptionKey,
  onChange,
  onSearchChange,
  inputPlaceholder = 'Search...',
  showDropdown = true,
  ariaLabel,
  dataTestId,
}: SearchBarProps<T>) {
  const [search, setSearch] = useState('');
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const searchbarRef = useRef<HTMLDivElement | null>(null);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearch(value);
    setDropdownOpen(true);
    onSearchChange?.(value);
  };

  const handleOptionClick = (option: T) => {
    setSearch(getOptionLabel(option));
    onChange?.(option);
    setDropdownOpen(false);
  };

  const handleClickOutside = () => {
    setDropdownOpen(false);
  };

  const filtered = filterOptions(options, search, getOptionLabel);
  const filteredOptions = sortOptions(filtered, search, getOptionLabel);

  return (
    <div className="SearchBar" ref={searchbarRef}>
      <div className="SearchBar__input-wrapper">
        <input
          value={search}
          onChange={handleSearchChange}
          onFocus={() => setDropdownOpen(true)}
          className="SearchBar__input"
          placeholder={inputPlaceholder}
          aria-label={ariaLabel || inputPlaceholder}
          data-testid={dataTestId}
        />
      </div>
      <div className="SearchBar__icon" aria-hidden="true">
        <SearchIcon />
      </div>
      <SearchBarDropdown
        anchorRef={searchbarRef}
        options={filteredOptions}
        onChange={handleOptionClick}
        onClickOutside={handleClickOutside}
        opened={showDropdown && isDropdownOpen}
        getOptionLabel={getOptionLabel}
        getOptionKey={getOptionKey}
        search={search}
        dataTestId={DataTestIds.COMMON.DROPDOWN}
      />
    </div>
  );
}
